/**
 * Sprezza Meals - Custom Meal Components
 *
 * Web components for the meal product page:
 * - SprezzaNutrition: Dynamic nutritional display with variant switching
 * - SprezzaQuantity: Hybrid quantity selector (stepper desktop / wheel mobile)
 */

/**
 * SprezzaNutrition - Nutritional display with variant-aware macro switching
 *
 * Listens for variant changes and swaps displayed macro values between
 * Standard and Double Protein data.
 */
class SprezzaNutrition extends HTMLElement {
  constructor() {
    super();
    this.nutritionData = null;
    this.currentVariant = 'standard';
    this.observer = null;
  }

  connectedCallback() {
    this.id = this.dataset.id;
    this.productHandle = this.dataset.productHandle;

    // Load nutrition data from embedded JSON
    this.loadNutritionData();

    // Watch for variant changes via the variant-json script element
    this.observeVariantChanges();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  loadNutritionData() {
    const jsonScript = document.getElementById(`nutrition-json-${this.id}`);
    if (jsonScript) {
      try {
        this.nutritionData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.warn('SprezzaNutrition: Failed to parse nutrition data', e);
      }
    }
  }

  observeVariantChanges() {
    // Watch the variant-json script element for changes (theme.js updates this)
    const variantScript = document.getElementById(`variant-json-${this.id}`);
    if (!variantScript) return;

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          this.handleVariantChange(variantScript.textContent);
        }
      }
    });

    this.observer.observe(variantScript, {
      childList: true,
      characterData: true,
      subtree: true
    });

    // Also listen for the theme's custom variant event if available
    if (this.productHandle) {
      window.addEventListener(`theme:product:${this.productHandle}:variant`, (e) => {
        if (e.detail?.variant) {
          this.handleVariantChange(JSON.stringify(e.detail.variant));
        }
      });
    }

    // Initial check
    this.handleVariantChange(variantScript.textContent);
  }

  handleVariantChange(variantJson) {
    if (!variantJson || !this.nutritionData) return;

    try {
      const variant = JSON.parse(variantJson);
      const title = (variant.public_title || variant.title || '').toLowerCase();

      // Determine if this is a double protein variant
      const isDoubleProtein = title.includes('double') || title.includes('extra');
      const newVariant = isDoubleProtein ? 'double_protein' : 'standard';

      if (newVariant !== this.currentVariant) {
        this.currentVariant = newVariant;
        this.updateDisplay();
      }
    } catch (e) {
      // Silent fail - variant JSON may be updating
    }
  }

  updateDisplay() {
    const data = this.nutritionData[this.currentVariant];
    if (!data) return;

    // Add transition class
    this.classList.add('sprezza-nutrition--transitioning');

    // Update each macro value
    const macroElements = {
      calories: this.querySelector('[data-value="calories"]'),
      protein: this.querySelector('[data-value="protein"]'),
      carbs: this.querySelector('[data-value="carbs"]'),
      fat: this.querySelector('[data-value="fat"]')
    };

    for (const [key, element] of Object.entries(macroElements)) {
      if (element && data[key] !== undefined) {
        element.textContent = data[key];
      }
    }

    // Remove transition class after animation
    setTimeout(() => {
      this.classList.remove('sprezza-nutrition--transitioning');
    }, 300);
  }
}


/**
 * SprezzaQuantity - Hybrid quantity selector
 *
 * Desktop: +/- stepper with numeric display
 * Mobile: iOS-style scroll-snap wheel picker
 *
 * Syncs to the hidden original quantity-selector input for theme.js compatibility.
 */
class SprezzaQuantity extends HTMLElement {
  constructor() {
    super();
    this.value = 1;
    this.min = 1;
    this.max = null;
    this.isMobile = false;
    this.mediaQuery = null;
    this.hiddenInput = null;
    this.wheelScrollTimeout = null;
  }

  connectedCallback() {
    this.id = this.dataset.id;
    this.productHandle = this.dataset.handle;
    this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : null;
    this.showContext = this.dataset.showContext === 'true';

    // Find the hidden original input
    this.findHiddenInput();

    // Setup media query for responsive switching
    this.setupMediaQuery();

    // Setup event listeners
    this.setupStepperEvents();
    this.setupWheelEvents();

    // Initialize display
    this.updateDisplay();
  }

  disconnectedCallback() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
    }
  }

  findHiddenInput() {
    // Look for the original quantity-selector input within the wrapper
    const wrapper = this.closest('.sprezza-quantity--root');
    if (wrapper) {
      this.hiddenInput = wrapper.querySelector('.quantity-selector--input');
      if (this.hiddenInput) {
        this.value = parseInt(this.hiddenInput.value, 10) || 1;

        // Listen for external changes to the hidden input
        this.hiddenInput.addEventListener('change', () => {
          const newValue = parseInt(this.hiddenInput.value, 10) || 1;
          if (newValue !== this.value) {
            this.value = newValue;
            this.updateDisplay();
          }
        });
      }
    }
  }

  setupMediaQuery() {
    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.handleMediaQueryChange = (e) => {
      this.isMobile = e.matches;
      this.updateViewMode();
    };
    this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
    this.isMobile = this.mediaQuery.matches;
    this.updateViewMode();
  }

  updateViewMode() {
    const stepper = this.querySelector('[data-view="desktop"]');
    const wheel = this.querySelector('[data-view="mobile"]');

    if (stepper) stepper.hidden = this.isMobile;
    if (wheel) wheel.hidden = !this.isMobile;

    // If switching to mobile, scroll wheel to current value
    if (this.isMobile) {
      this.scrollWheelToValue(this.value, false);
    }
  }

  setupStepperEvents() {
    const minusBtn = this.querySelector('.sprezza-quantity--minus');
    const plusBtn = this.querySelector('.sprezza-quantity--plus');

    if (minusBtn) {
      minusBtn.addEventListener('click', () => this.decrement());
    }
    if (plusBtn) {
      plusBtn.addEventListener('click', () => this.increment());
    }

    // Keyboard support for display element
    const display = this.querySelector('.sprezza-quantity--display');
    if (display) {
      display.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.increment();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.decrement();
        }
      });
    }
  }

  setupWheelEvents() {
    const wheel = this.querySelector('.sprezza-quantity--wheel');
    if (!wheel) return;

    // Scroll event for wheel picker
    wheel.addEventListener('scroll', () => {
      // Debounce to detect when scrolling stops
      clearTimeout(this.wheelScrollTimeout);
      this.wheelScrollTimeout = setTimeout(() => {
        this.handleWheelScrollEnd(wheel);
      }, 100);
    });

    // Keyboard navigation for wheel
    wheel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.decrement();
        this.scrollWheelToValue(this.value);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.increment();
        this.scrollWheelToValue(this.value);
      }
    });

    // Click on wheel item
    wheel.querySelectorAll('.sprezza-quantity--wheel-item').forEach(item => {
      item.addEventListener('click', () => {
        const newValue = parseInt(item.dataset.value, 10);
        this.setValue(newValue);
        this.scrollWheelToValue(newValue);
      });
    });
  }

  handleWheelScrollEnd(wheel) {
    // Calculate which item is currently centered
    const itemHeight = 50; // Match CSS
    const scrollTop = wheel.scrollTop;
    const centerIndex = Math.round(scrollTop / itemHeight);
    const newValue = centerIndex + 1; // 1-indexed

    if (newValue >= this.min && (!this.max || newValue <= this.max)) {
      this.setValue(newValue);
    }

    // Snap to exact position
    this.scrollWheelToValue(this.value);
  }

  scrollWheelToValue(value, smooth = true) {
    const wheel = this.querySelector('.sprezza-quantity--wheel');
    if (!wheel) return;

    const itemHeight = 50; // Match CSS
    const targetScroll = (value - 1) * itemHeight;

    wheel.scrollTo({
      top: targetScroll,
      behavior: smooth ? 'smooth' : 'auto'
    });

    // Update aria-selected
    wheel.querySelectorAll('.sprezza-quantity--wheel-item').forEach(item => {
      const isSelected = parseInt(item.dataset.value, 10) === value;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.classList.toggle('sprezza-quantity--wheel-item-selected', isSelected);
    });
  }

  increment() {
    if (this.max && this.value >= this.max) {
      this.showMaxMessage();
      return;
    }
    this.setValue(this.value + 1);
  }

  decrement() {
    if (this.value <= this.min) return;
    this.setValue(this.value - 1);
    this.hideMaxMessage();
  }

  setValue(newValue) {
    // Clamp value
    newValue = Math.max(this.min, newValue);
    if (this.max) {
      newValue = Math.min(this.max, newValue);
    }

    if (newValue === this.value) return;

    this.value = newValue;
    this.syncToHiddenInput();
    this.updateDisplay();

    // Check if max reached
    if (this.max && this.value >= this.max) {
      this.showMaxMessage();
    } else {
      this.hideMaxMessage();
    }
  }

  syncToHiddenInput() {
    if (!this.hiddenInput) return;

    this.hiddenInput.value = this.value;

    // Dispatch events for theme.js compatibility
    this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Dispatch theme's custom event if product handle is available
    if (this.productHandle) {
      window.dispatchEvent(new CustomEvent(`theme:product:${this.productHandle}:updateQuantity`, {
        detail: { quantity: this.value }
      }));
    }
  }

  updateDisplay() {
    // Update stepper display
    const display = this.querySelector('.sprezza-quantity--display');
    if (display) {
      display.textContent = this.value;
    }

    // Update button states
    const minusBtn = this.querySelector('.sprezza-quantity--minus');
    const plusBtn = this.querySelector('.sprezza-quantity--plus');

    if (minusBtn) {
      minusBtn.disabled = this.value <= this.min;
      minusBtn.setAttribute('aria-disabled', this.value <= this.min ? 'true' : 'false');
    }
    if (plusBtn) {
      const atMax = this.max && this.value >= this.max;
      plusBtn.disabled = atMax;
      plusBtn.setAttribute('aria-disabled', atMax ? 'true' : 'false');
    }

    // Update wheel selection if mobile
    if (this.isMobile) {
      this.scrollWheelToValue(this.value, false);
    }
  }

  showMaxMessage() {
    const message = this.querySelector('.sprezza-quantity--max-message');
    if (message) {
      message.hidden = false;
    }
  }

  hideMaxMessage() {
    const message = this.querySelector('.sprezza-quantity--max-message');
    if (message) {
      message.hidden = true;
    }
  }

  /**
   * Update max value dynamically (for WS-3 subscription enforcement)
   * @param {number} newMax - New maximum value
   */
  setMax(newMax) {
    this.max = newMax;
    this.dataset.max = newMax;

    // Re-clamp current value if needed
    if (this.value > this.max) {
      this.setValue(this.max);
    }

    this.updateDisplay();
  }

  /**
   * Update subscription context display (for WS-3)
   * @param {number} current - Current meal count
   * @param {number} max - Maximum meals for tier
   */
  setSubscriptionContext(current, max) {
    const context = this.querySelector('.sprezza-quantity--sub-context');
    if (!context) return;

    const currentEl = context.querySelector('[data-current]');
    const maxEl = context.querySelector('[data-max]');
    const progressBar = context.querySelector('.sprezza-quantity--sub-progress-bar');

    if (currentEl) currentEl.textContent = current;
    if (maxEl) maxEl.textContent = max;
    if (progressBar) {
      const percentage = max > 0 ? Math.min(100, (current / max) * 100) : 0;
      progressBar.style.width = `${percentage}%`;
    }

    context.hidden = false;
  }
}


// Register custom elements
if (!customElements.get('sprezza-nutrition')) {
  customElements.define('sprezza-nutrition', SprezzaNutrition);
}

if (!customElements.get('sprezza-quantity')) {
  customElements.define('sprezza-quantity', SprezzaQuantity);
}
